import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  created_at: string;
  metadata?: {
    listing_id?: string;
    listing_title?: string;
  };
}

const NotificationBell = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }

      if (data) {
        console.log('Fetched notifications:', data);
        setNotifications(data);
        const unreadNotifications = data.filter(n => !n.read);
        console.log('Unread notifications count:', unreadNotifications.length);
        setUnreadCount(unreadNotifications.length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase.rpc('mark_notification_read', {
        notification_id: notificationId
      });

      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }

      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc('mark_all_notifications_read');

      if (error) {
        console.error('Error marking all notifications as read:', error);
        toast.error('Failed to mark notifications as read');
        return;
      }

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark notifications as read');
    } finally {
      setLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.type === 'listing_approved' && notification.metadata?.listing_id) {
      navigate(`/listing/${notification.metadata.listing_id}`);
    } else if (notification.type === 'kyc_approved') {
      navigate('/account');
    }
    // Add more navigation cases as needed
  };

  // Set up polling instead of real-time subscription
  useEffect(() => {
    if (!user) return;

    console.log('Setting up notification polling for user:', user.id);
    fetchNotifications();

    // Poll every 30 seconds for new notifications
    const pollingInterval = setInterval(() => {
      console.log('Polling for notifications...');
      fetchNotifications();
    }, 30000);

    return () => {
      console.log('Cleaning up notification polling');
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [user]);

  if (!user) return null;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'listing_approved':
        return '🏠';
      case 'kyc_approved':
        return '✅';
      default:
        return '📢';
    }
  };

  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
            <Bell className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center border-2 border-white z-50 pointer-events-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        <DropdownMenuContent className="w-80" align="end" forceMount>
          <div className="flex items-center justify-between p-2">
            <h3 className="font-semibold">
              {t('notifications', 'الإشعارات', 'Notifications')}
              {unreadCount > 0 && (
                <span className="ml-2 text-sm text-muted-foreground">
                  ({unreadCount})
                </span>
              )}
              <span className="ml-2 text-xs text-blue-600" title="Using polling for updates">
                📊
              </span>
            </h3>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead}
                disabled={loading}
                className="text-xs"
              >
                {t('mark_all_read', 'تحديد الكل كمقروء', 'Mark all read')}
              </Button>
            )}
          </div>
          <DropdownMenuSeparator />
          
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              {t('no_notifications', 'لا توجد إشعارات', 'No notifications')}
            </div>
          ) : (
            <ScrollArea className="h-96">
              {notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className={`p-3 cursor-pointer transition-colors ${
                    !notification.read 
                      ? 'bg-blue-50 hover:bg-blue-100 border-l-2 border-blue-500' 
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3 w-full">
                    <span className="text-lg mt-1 flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm truncate ${
                          !notification.read ? 'font-semibold' : 'font-medium'
                        }`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0 animate-pulse" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </ScrollArea>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default NotificationBell;
